/**
 * OpenSource — public "Open Source" page.
 *
 * Audience: developers/community. This page is entirely about Loudin, the
 * open source access control management app Simkura sponsors — its mission,
 * where to find it (GitHub repo + project site), and who to contact for
 * help. Third-party hardware onboarding lives on its own page now (/hardware)
 * rather than as a second track here.
 *
 * Sponsored projects are data-driven (SPONSORED_PROJECTS) and HIDDEN until the
 * real project details (name, repo URL, sponsorship terms) are confirmed —
 * same convention as the Partner page's PARTNERS list. With the array empty,
 * the section renders its "announcement coming" state.
 */

import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  IconArrowRight,
  IconExternalLink,
  IconBrandGithub,
  IconWorld,
} from '@tabler/icons-react';
import { PublicLayout } from '../../components/PublicLayout';

const CONTACT_EMAIL = 'info@simkura.com';
const LOUDIN_CONTACT_EMAIL = 'Jack@simkura.com';

// Sponsored open source projects.
const SPONSORED_PROJECTS: Array<{
  id: string;
  name: string;
  tagline: string;
  sponsorship: string;
  url: string;
  websiteUrl: string;
}> = [
  {
    id: 'loudin',
    name: 'Loudin',
    tagline: 'Open source (MIT) access control management — a multi-tenant web app for managing smart door locks, the people who use them, and the credentials that open them.',
    sponsorship: 'Sponsored and supported by Simkura, with Simkura Core as the device backbone.',
    url: 'https://github.com/simkura-tech/loudin',
    websiteUrl: 'https://loudin-project.com',
  },
];

// ── Layout primitives (shared idiom with the other public pages) ──────────────

const Container = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 clamp(20px, 6vw, 64px);
`;

const Section = styled.section`
  padding: clamp(40px, 7vh, 80px) 0;
`;

// ── Hero — washed text hero, matching Home. No hero card, no hero image.

const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(56px, 11vh, 120px) 0 clamp(32px, 5vh, 56px);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background:
      radial-gradient(60% 80% at 20% 0%, #d8e4f0 0%, transparent 60%),
      radial-gradient(50% 70% at 85% 10%, #dddcea 0%, transparent 55%),
      radial-gradient(40% 50% at 60% 100%, #c9d6df22 0%, transparent 70%);
    pointer-events: none;
  }
`;

const Hero = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Eyebrow = styled.span`
  display: block;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => '#0d253d'};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const HeroHeadline = styled.h1`
  font-size: clamp(34px, 5.5vw, 56px);
  line-height: 1.06;
  letter-spacing: -0.025em;
  font-weight: 700;
  color: ${({ theme }) => theme.font.color.primary};
  margin: 0 0 ${({ theme }) => theme.spacing(5)} 0;
  max-width: 16ch;
`;

const HeroSub = styled.p`
  font-size: clamp(16px, 2vw, 19px);
  line-height: 1.55;
  color: ${({ theme }) => theme.font.color.secondary};
  margin: 0;
  max-width: 46ch;
`;

const SectionLabel = styled.p`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ theme }) => '#0d253d'};
  margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
`;

const SectionTitle = styled.h2`
  font-size: clamp(24px, 3.4vw, 34px);
  line-height: 1.12;
  letter-spacing: -0.02em;
  font-weight: 700;
  color: ${({ theme }) => theme.font.color.primary};
  margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
`;

const SectionLede = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.font.color.secondary};
  line-height: 1.6;
  max-width: 640px;
  margin: 0 0 ${({ theme }) => theme.spacing(8)} 0;
`;

// ── Sponsored project — full-width card ───────────────────────────────────────

const ProjectCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
  gap: clamp(24px, 5vw, 64px);
  padding: clamp(32px, 5vw, 56px);
  border-radius: ${({ theme }) => theme.border.radius.xl};
  background: ${({ theme }) => theme.background.primary};
  border: 1px solid ${({ theme }) => theme.border.color.medium};

  @media (max-width: 780px) {
    grid-template-columns: 1fr;
  }

  .top {
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(4)};
    margin-bottom: ${({ theme }) => theme.spacing(5)};
  }
  .badge {
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => '#d8e4f0'};
    color: ${({ theme }) => '#0d253d'};
  }
  .name {
    font-size: 26px;
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    color: ${({ theme }) => theme.font.color.primary};
    margin: 0;
  }
  .tagline {
    font-size: 16px;
    line-height: 1.6;
    color: ${({ theme }) => theme.font.color.secondary};
    margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
  }
  .sponsorship {
    font-size: 14px;
    line-height: 1.6;
    color: ${({ theme }) => theme.font.color.tertiary};
    margin: 0;
  }
  .panel {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing(4)};
    padding: clamp(20px, 3vw, 32px);
    border-radius: ${({ theme }) => theme.border.radius.lg};
    background: ${({ theme }) => theme.background.secondary};
  }
  .links {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};
  }
  .links a {
    display: inline-flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(2)};
    font-size: 15px;
    font-weight: ${({ theme }) => theme.font.weight.semiBold};
    color: ${({ theme }) => '#0d253d'};
    text-decoration: none;
  }
  .links a:hover { text-decoration: underline; }
  .contact {
    font-size: 13px;
    color: ${({ theme }) => theme.font.color.tertiary};
    margin: 0;
    padding-top: ${({ theme }) => theme.spacing(3)};
    border-top: 1px solid ${({ theme }) => theme.border.color.light};
  }
  .contact a { color: ${({ theme }) => theme.font.color.secondary}; }
`;

const EmptyState = styled.div`
  border: 1px dashed ${({ theme }) => theme.border.color.strong};
  border-radius: ${({ theme }) => theme.border.radius.lg};
  background: ${({ theme }) => theme.background.secondary};
  padding: clamp(28px, 5vw, 48px);
  text-align: center;
  color: ${({ theme }) => theme.font.color.tertiary};
  font-size: 15px;
`;

// ── Closing CTA band ──────────────────────────────────────────────────────────

const CtaBand = styled.div`
  text-align: center;
  padding: clamp(44px, 7vh, 76px) 24px;
  border-radius: ${({ theme }) => theme.border.radius.xl};
  background: linear-gradient(135deg, #1b3a5e 0%, #0d253d 100%);
  color: #fff;

  h2 {
    font-size: clamp(26px, 4vw, 40px);
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 700;
    color: #fff;
    margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
  }
  p {
    color: rgba(255, 255, 255, 0.88);
    font-size: 18px;
    line-height: 1.55;
    margin: 0 auto ${({ theme }) => theme.spacing(7)};
    max-width: 52ch;
  }
`;

const CtaActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};
  justify-content: center;
  align-items: center;
`;

const CtaWhite = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 52px;
  padding: 0 26px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  background: #fff;
  color: ${({ theme }) => '#0d253d'};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-size: 16px;
  text-decoration: none;
  transition: transform 0.15s ease;

  &:hover { transform: translateY(-1px); }
`;

const CtaGhost = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  height: 52px;
  padding: 0 22px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  border: 1px solid rgba(255, 255, 255, 0.55);
  color: #fff;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-size: 16px;
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover { background: rgba(255, 255, 255, 0.12); }
`;

// ── Page ───────────────────────────────────────────────────────────────────────

function OpenSource() {
  return (
    <PublicLayout>
      {/* Hero — washed text hero */}
      <HeroSection>
        <Container>
          <Hero>
            <Eyebrow>Open source</Eyebrow>
            <HeroHeadline>Built to connect — not to lock in.</HeroHeadline>
            <HeroSub>
              We sponsor Loudin, an open source access control management
              app, so anyone can run a real deployment on Simkura hardware
              without being locked into our dashboard.
            </HeroSub>
          </Hero>
        </Container>
      </HeroSection>

      {/* Loudin — the sponsored open source app */}
      <Section id="sponsored">
        <Container>
          <SectionLabel>Project we sponsor</SectionLabel>
          <SectionTitle>An open source access control management app</SectionTitle>
          <SectionLede>
            Loudin is an MIT-licensed open source access control management
            application for managing smart door locks, the people who use
            them, and the credentials that open them — with Simkura Core as
            the device backbone behind it. Our mission in sponsoring it is
            simple: make sure there's always a free, open path to run a real
            Simkura deployment, not just our own dashboard.
          </SectionLede>

          {SPONSORED_PROJECTS.length > 0 ? (
            SPONSORED_PROJECTS.map((p) => (
              <ProjectCard key={p.id}>
                <div>
                  <div className="top">
                    <span className="badge"><IconBrandGithub size={28} /></span>
                    <h3 className="name">{p.name}</h3>
                  </div>
                  <p className="tagline">{p.tagline}</p>
                  <p className="sponsorship">{p.sponsorship}</p>
                </div>
                <div className="panel">
                  <div className="links">
                    <a href={p.url} target="_blank" rel="noreferrer">
                      <IconBrandGithub size={16} /> GitHub repo <IconExternalLink size={13} />
                    </a>
                    <a href={p.websiteUrl} target="_blank" rel="noreferrer">
                      <IconWorld size={16} /> Loudin Project site <IconExternalLink size={13} />
                    </a>
                  </div>
                  <p className="contact">
                    For assistance, reach out to{' '}
                    <a href={`mailto:${LOUDIN_CONTACT_EMAIL}`}>{LOUDIN_CONTACT_EMAIL}</a>
                  </p>
                </div>
              </ProjectCard>
            ))
          ) : (
            <EmptyState>
              We're finalizing the announcement of the access control
              management app we sponsor — details and the repository link are
              coming soon. Building in this space?{' '}
              <a href={`mailto:${CONTACT_EMAIL}`}>We'd like to hear about it.</a>
            </EmptyState>
          )}
        </Container>
      </Section>

      {/* Closing CTA */}
      <Section>
        <Container>
          <CtaBand>
            <h2>Building on Simkura?</h2>
            <p>
              Whether you're contributing to Loudin or integrating Simkura
              into your own software, we'd like to hear about it.
            </p>
            <CtaActions>
              <CtaWhite to="/developers">
                Explore the Simkura API
                <IconArrowRight size={18} />
              </CtaWhite>
              <CtaGhost to="/contact">Contact us</CtaGhost>
            </CtaActions>
          </CtaBand>
        </Container>
      </Section>
    </PublicLayout>
  );
}

export default OpenSource;
